import React, { useMemo } from 'react'
import { Tag, TreeSelect } from 'antd'
import { isEmpty } from 'lodash'

const { SHOW_CHILD } = TreeSelect

/**
 * ProjectCategory — รองรับ 2 โหมด:
 *  1. (ใหม่) รับ `data` เป็น flat array จาก GET_PROJECT_CATEGORY
 *     พร้อม `selectedNameMap` ({ "id": "name" }) สำหรับ inactive items
 *  2. (เดิม) รับ `masterData` แบบ nested tree (backward-compatible)
 */
const ProjectCategory = (props) => {
  const {
    // props ใหม่
    data,
    selectedNameMap,
    // props เดิม (backward-compatible)
    masterData,
    // shared props
    choiceCompany,
    choiceProjectGroup,
    choiceProjectCategory,
    setChoiceProjectCategory,
    setAccountOptions,
    RebaseCutterFunctionally,
    disabled,
  } = props

  let url_page = window.location.pathname

  const isInfoPage =
    !url_page.includes('/management/powerbi') &&
    !url_page.includes('/create-project') &&
    !url_page.includes('/information')

  // ─── โหมดใหม่: flat data จาก API ───────────────────────────────────────
  const flatTreeData = useMemo(() => {
    if (!data) return null

    const activeIds = new Set(data.map((item) => String(item.id)))

    const activeOptions = data.map((item) => ({
      title: item.name_en || item.name,
      value: String(item.id),
      key: String(item.id),
      disabled: false,
    }))

    // inactive items — แสดงใน dropdown เฉพาะ view mode (disabled), edit mode ซ่อนออก
    const inactiveOptions = isInfoPage
      ? (choiceProjectCategory || [])
          .filter((id) => !activeIds.has(String(id)))
          .map((id) => ({
            title: selectedNameMap?.[String(id)] || String(id),
            value: String(id),
            key: String(id),
            disabled: true,
          }))
      : []

    return [...activeOptions, ...inactiveOptions]
  }, [data, choiceProjectCategory, selectedNameMap, isInfoPage])

  // ─── โหมดเดิม: nested tree (backward-compatible) ──────────────────────
  const legacyTreeData = useMemo(() => {
    if (!masterData) return []

    // filter ตาม company แล้ว group แล้ว flatten category level ออกมา
    const filteredByCompany = masterData.filter((item) =>
      choiceCompany?.includes(item?.value),
    )
    const categories = []
    filteredByCompany.forEach((parent, idxParent) => {
      parent.children?.forEach((groupChild, idxGroup) => {
        if (choiceProjectGroup?.includes(groupChild.value)) {
          groupChild.children?.forEach((cat, idxCat) => {
            categories.push({
              ...cat,
              key: `0-${idxParent}-${idxGroup}-${idxCat}`,
              disabled: false,
            })
          })
        }
      })
    })

    const activeIds = new Set(categories.map((c) => c.value))
    const inactiveItems = (choiceProjectCategory || [])
      .filter((id) => !activeIds.has(String(id)))
      .map((id) => ({
        title: selectedNameMap?.[String(id)] || String(id),
        value: String(id),
        key: `inactive-${id}`,
        disabled: true,
      }))

    return [...categories, ...inactiveItems]
  }, [masterData, choiceCompany, choiceProjectGroup, choiceProjectCategory, selectedNameMap])

  const treeData = flatTreeData !== null ? flatTreeData : legacyTreeData

  function onChange(value) {
    if (setAccountOptions) setAccountOptions([])
    if (RebaseCutterFunctionally) {
      RebaseCutterFunctionally('from project cat', choiceCompany, choiceProjectGroup, value)
    }
    setChoiceProjectCategory(value)
  }

  const tProps = {
    treeData,
    value: choiceProjectCategory,
    onChange,
    treeCheckable: true,
    showCheckedStrategy: SHOW_CHILD,
    placeholder: 'Select Strategic Direction',
    style: { width: '100%' },
  }

  if (isInfoPage) {
    return (
      <TreeSelect
        size="large"
        treeDefaultExpandAll={true}
        {...tProps}
        maxTagCount={0}
        maxTagPlaceholder={(event) => event[0]?.label}
        disabled={isEmpty(choiceProjectGroup)}
        tagRender={(event) => {
          const { label } = event
          return (
            <Tag
              style={{
                display: 'flex',
                fontSize: 12,
                borderRadius: '16px',
                background: 'rgba(7, 78, 159, 0.1)',
                height: '32px',
                alignItems: 'center',
                color: '#074E9F',
              }}
            >
              {choiceProjectCategory?.length > 1
                ? 'Multi select strategic direction'
                : label}
            </Tag>
          )
        }}
      />
    )
  }

  // หน้า edit — inactive items ไม่อยู่ใน dropdown แต่แสดงเป็น tag ปกติผ่าน labelInValue
  const labeledValue = (choiceProjectCategory || []).map((id) => ({
    label:
      treeData.find((item) => item.value === id)?.title ??
      (selectedNameMap?.[id] || id),
    value: id,
  }))

  return (
    <TreeSelect
      size="large"
      treeDefaultExpandAll={true}
      {...tProps}
      labelInValue={true}
      value={labeledValue}
      onChange={(values) => {
        const ids = values.map((v) => v.value)
        if (setAccountOptions) setAccountOptions([])
        if (RebaseCutterFunctionally) {
          RebaseCutterFunctionally('from project cat', choiceCompany, choiceProjectGroup, ids)
        }
        setChoiceProjectCategory(ids)
      }}
      disabled={disabled}
    />
  )
}

export default ProjectCategory
